import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { IonicModule, LoadingController } from '@ionic/angular';
import { ToastController } from '@ionic/angular';
import { Analysis, BeerProfileRequest, BeerStat, SearchCharacteristicWithType, SearchType, AnalysisItem, LocalCharacteristic, Venue, SimilarBeerRequest, BaseFlavors} from "../../models/models";
import { AnalysisServiceProvider } from '../../providers/analysis-service/analysis-service';
import { LocationServiceProvider } from "../../providers/location-service/location-service";
import { UserDataProvider } from '../../providers/user-data/user-data';
import { BeerServiceProvider } from '../../providers/beer-service/beer-service';
import { BhutilProvider } from '../../providers/bhutil/bhutil';
import { AnalysisHolderProvider } from '../../providers/analysis-holder/analysis-holder';
import { RefdataProvider } from '../../providers/refdata/refdata';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';
import { AppEventService } from '../../app/app-events';
import { SimilarBeersComponent } from '../similar-beers/similar-beers';
import { BeerSpiderComponent } from '../beer-spider/beer-spider';
import { BeerCcComponent } from '../beer-cc/beer-cc';
import { FlavorSliderAnalysisComponent } from '../flavor-slider/flavor-slider-analysis';
import { BeerHiveComponent } from '../beer-hive/beer-hive';
import { BeerIbaComponent } from '../beer-iba/beer-iba';
import { BeerAromasComponent } from '../beer-aromas/beer-aromas';
import { RatingBarComponent } from '../rating-bar/rating-bar';
import { WishlistButtonComponent } from '../wishlist-button/wishlist-button';
import { CommonModule } from '@angular/common';
import { MyNotesComponent } from '../my-notes/my-notes';
import { FormsModule } from '@angular/forms';
import { BeerAnalysisMyComponent } from './beer-analysis-my';

/**
 * Generated class for the BeerAnalysisMyComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: 'beer-analysis-my-summary',
  templateUrl: 'beer-analysis-my-summary.html',
  styleUrls: ['./beer-analysis-my.scss'],
  standalone: false,
  imports: [
    IonicModule,
    TranslateModule,
    SimilarBeersComponent,
    BeerSpiderComponent,
    BeerCcComponent,
    FlavorSliderAnalysisComponent,
    BeerHiveComponent,
    BeerIbaComponent,
    BeerAromasComponent,
    RatingBarComponent,
    WishlistButtonComponent,
    CommonModule,
    FormsModule,
    MyNotesComponent
  ]
})

export class BeerAnalysisMySummaryComponent extends BeerAnalysisMyComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  /*
   *  attributes to manage the gui appearance
   */
  loading: any = null; // will contain the "loading" gui element reference

  bShouldShow: boolean;
  ignoreTotalExperienceValue: boolean;
  shouldIgnoreSliderUpdate: boolean;
  saveActive: boolean = false;
  ratingBarActive: boolean = false;
  aromasActive: boolean = false;

  /*
   *  transport objects to convey data from and to the gui
   */
  currentBaseFlavors: BaseFlavors = {
    sweet: 0,
    sour: 0,
    bitter: 0,
    fruity: 0,
    hoppy: 0,
    malty: 0,
    color: 0,
    clarity: 0
  };
  analysisCharacteristics: Array<SearchCharacteristicWithType>;  // Array of typed key/value pairs
  characteristicsKeyNames: Array<string> = undefined;  // only keys, for passing to <beer-aromas> as input
  totalexperience: number;
  totalexperienceGUIValue: number;
  notes: string;

  /*
   *  the model: beerID and analysis objects
   */
  theBeerID: string;

  // translation objects
  wishlistlabel: string;

  /*
   *  other local attributes
   */
  // contains nbr of analyses per group (currently always 1 for group "brewer" and >=1 for group "beerhive")
  @Input()
  set beerID(bID: string) {
    this.theBeerID = bID;
    this.updateBeerID(bID);
  }
  get beerID() {
    return this.theBeerID;
  }

  private postAnalysisSubscription: any;
  private showBeerSubscription: any;
  private wishlistPresentSubscription: any;
  private analyseColourInputSubscription: any;
  private analysisTotalexperienceSubscription: any;
  private updateNotesSubscription: any;
  private saveBeerSubscription: any;
  venueSelectedSubscription: any;
  venueDeselectedSubscription: any;
  private showSaveButtonSubscription: any;
  private doSaveSubscription: any;


  constructor(
    protected toastController: ToastController,
    protected events: AppEventService,
    protected analysisService: AnalysisServiceProvider,
    private locationService: LocationServiceProvider,
    private userData: UserDataProvider,
    private beerService: BeerServiceProvider,
    protected bhutil: BhutilProvider,
    private loadingCtrl: LoadingController,
    protected beerAnalysisHolder: AnalysisHolderProvider,
    private refdata: RefdataProvider,
    protected translateService: TranslateService,
    private storage: Storage
  ) {
    super(toastController, events, analysisService, bhutil, beerAnalysisHolder, translateService);

    this.wishlistlabel = translateService.instant('BEERANALYSIS.WISHLIST') + " +";

    this.bShouldShow = true;

    this.ignoreTotalExperienceValue = false;
    this.shouldIgnoreSliderUpdate = false;

    //default slider values
    this.currentBaseFlavors.sweet = -1;
    this.currentBaseFlavors.sour = -1;
    this.currentBaseFlavors.bitter = -1;
    this.currentBaseFlavors.hoppy = -1;
    this.currentBaseFlavors.fruity = -1;
    this.currentBaseFlavors.malty = -1;

    //analysis info
    this.totalexperience = null;
    this.totalexperienceGUIValue = null;

    this.currentBaseFlavors.color = -1;
    this.currentBaseFlavors.clarity = 1;

    this.wishlistPresentSubscription = (beerInWishlist: boolean) => {
      if (beerInWishlist) {
        this.wishlistlabel = translateService.instant('BEERANALYSIS.WISHLIST') + ' -';
      }
      else {
        this.wishlistlabel = translateService.instant('BEERANALYSIS.WISHLIST') + ' +';
      }
    };

    this.updateNotesSubscription = (updatedNotes: string) => {
      this.log("updateNotesSubscription called with #" + updatedNotes + "#");
      this.updateNotesTransferObjectAndModel(updatedNotes);
    };

    this.analysisTotalexperienceSubscription = (newTotalExperience: number) => {
      this.log("analysisTotalexperienceSubscription called with " + newTotalExperience);
      if (this.ignoreTotalExperienceValue) {
        this.log("ignoring this totalexperience update");
        this.ignoreTotalExperienceValue = false;
      } else {
        this.updateTotalExperienceModelFromGUI(newTotalExperience);
      }
    };

    this.showBeerSubscription = () => {
      this.log("showBeerSubscription called");
      // needed???
      this.clearTransferObjectsAndModel();

      // 2 Fill the model
      // 2A if the beer profile is not yet loaded, load the beer profile
      // 2B if the beer profile is already loaded, switch the analysisHolder to the beer
      if (this.beerAnalysisHolder.hasActiveBeer()) {
        this.updateBeerID(this.beerAnalysisHolder.getActiveBeer());

        if (this.beerAnalysisHolder.hasBeer(this.theBeerID)) {
          console.log("Beeranalysiscomponent.showBeerSubscription: switch to beer " + this.theBeerID);
          this.beerAnalysisHolder.switchToBeer(this.theBeerID);

          this.loadMyAnalysis(false);

          // 3 Update the GUI elements, fill the transfer objects and publish the updates to the GUI
          this.updateView();
        }
        else {
          console.log("BeeranalysisMyComponent.showBeerSubscription: no beer profile");
        }
      }
      else {
          console.log("BeeranalysisMyComponent.showBeerSubscription: no active beer");
      }
    };

    this.analyseColourInputSubscription = (colourCharacteristic: SearchCharacteristicWithType) => {
      this.log("analyseColourInputSubscription called with " + JSON.stringify(colourCharacteristic));

      this.currentBaseFlavors.color = colourCharacteristic.characteristic.value.valueOf();
      this.beerAnalysisHolder.getMyAnalysis().colour = colourCharacteristic.characteristic.value;
      this.beerAnalysisHolder.checkForDifferences();
    };

    this.postAnalysisSubscription = (characteristics: Array<SearchCharacteristicWithType>) => {
      this.log("postAnalysisSubscription called - SUB post:analysis");
      console.dir(characteristics);

      // update transfer objects
      this.analysisCharacteristics = characteristics;

      // update keyNames and prepare key/value pairs and mouthfeel
      let tempCharacteristicsKeyNames = [];
      let tempCharacteristicsKeyValuePairs = [];
      let intensity: number | undefined = undefined;
      let body: number | undefined = undefined;
      let astringent: number | undefined = undefined;
      let alcohol: number | undefined = undefined;
      let carbonation: number | undefined = undefined;
      for (let aChar of characteristics) {
        let charIndex = aChar.characteristic.key.indexOf("_");//used to be aChar.indexOf()???
        if (charIndex != -1) {
          // there is an _ in the item name => aroma, ...
          // check for value (do not add if value == 0) => this means the item still has to be removed
          if (aChar.characteristic.value.valueOf() > 0) {
            // fix for bug BEERHIVE-95 ???
            console.log("postAnalysisSubscription: ADDing " + aChar.characteristic.key);
            tempCharacteristicsKeyNames.push(aChar.characteristic.key);
            tempCharacteristicsKeyValuePairs.push({ name: aChar.characteristic.key, value: aChar.characteristic.value});
          }
          else {
            // not adding this item
            console.log("postAnalysisSubscription: NOT adding " + aChar.characteristic.key);
          }
        }
        else {
          switch (aChar.characteristic.key) {
            case "intensity": intensity = aChar.characteristic.value.valueOf(); break;
            case "body": body = aChar.characteristic.value.valueOf(); break;
            case "astringent": astringent = aChar.characteristic.value.valueOf(); break;
            case "alcohol": alcohol = aChar.characteristic.value.valueOf(); break;
            case "carbonation": carbonation = aChar.characteristic.value.valueOf(); break;
            default: break;
          }
        }
      }
      console.log("[beer-analysis-my-summary.postAnalysisSubscription] - characteristics are: " + characteristics.join(","));
      this.characteristicsKeyNames = tempCharacteristicsKeyNames;

      // update the model
      // - binary aromas
      // - intensity, body, astringent, alcohol, carbonation
      //
      // the model has 2 parts:
      // - characteristics: the 8 main binary aromas
      // - characteristicsvalues: array of name/value pairs
      this.beerAnalysisHolder.getMyAnalysis().characteristics = tempCharacteristicsKeyNames;
      this.beerAnalysisHolder.getMyAnalysis().characteristicsvalues = tempCharacteristicsKeyValuePairs;
      if (intensity) this.beerAnalysisHolder.getMyAnalysis().intensity = intensity;
      if (body) this.beerAnalysisHolder.getMyAnalysis().body = body;
      if (astringent) this.beerAnalysisHolder.getMyAnalysis().astringent = astringent;
      if (alcohol) this.beerAnalysisHolder.getMyAnalysis().alcohol = alcohol;
      if (carbonation) this.beerAnalysisHolder.getMyAnalysis().carbonation = carbonation;
      this.beerAnalysisHolder.checkForDifferences();
    };
    this.analysisCharacteristics = [];
  }
  private clearTransferObjectsAndModel() {
    console.log("beer-analysis-my - clearTransferObjectsAndModel called");
    this.wishlistlabel = this.translateService.instant('BEERANALYSIS.WISHLIST') + ' +';
    // 1 Make the context clean:
    //   - empty the model: deactivate the previous beer profile from the analysis holder
    //   - empty the transfer objects and the view
    // 2 Fill the model
    // 2A if the beer profile is not yet loaded, load the beer profile
    // 2B if the beer profile is already loaded, switch the analysisHolder to the beer
    //
    // 3 Fill the transfer objects and publish the updates to the GUI
    // 1 Make the context clean:

    //   - empty the transfer objects and the view
    // intensity/body/astringency
    this.clearIBATransferObjectsAndPublishEvent();
    //spider values
    console.log("clearspidervalues 1");
    this.clearSpiderValuesTransferObjectandPublishEvent();
    //aromas
    this.clearAromasTransferObjectsandPublishEvent();
    //total experience
    this.clearTotalExperienceTransferObjectAndPublishEvent();
    //notes
    this.updateNotesTransferObjectAndModel("");

    // inform beer-hive component
    this.events.publish("clear:analysis", true);

    this.beerAnalysisHolder.shouldCheck = false;
  }

  // determine which group to show after loading the analysesfrom the server
  initialAnalysisInstanceToShow(): string {
    if (this.beerAnalysisHolder.hasBeerhiveAnalysis())
      return "beerhive";
    else
      return "my";
  }

  ionViewWillEnter() {
    console.log("beer-analysis-my - ionViewWillEnter called");
    if (this.refdata.isAndroid()) {
      console.log('setting fixed position dynamically for android');
      let saveButtonEl: HTMLElement = document.getElementById('save');
      saveButtonEl.style.position = 'fixed';
      // critcontainerEl.style.zIndex = '100';
    }
  }

  ngAfterViewInit() {
    this.log("ngAfterViewInit called");
    if (this.beerAnalysisHolder.hasActiveBeer())
      this.showMyAnalysis(false);

    /*this.storage.get('selected-venue').then((venue) => {
      // First read the selected venue from storage
      let venueJSON = JSON.parse(venue);
      if (venueJSON != null) {
        this.selectedVenue = venueJSON;
        console.log("[my-analyses] - currently venue with id selected: " + venueJSON.id);
      }
    });*/
  }

  updateNotesTransferObjectAndModel(updatedNotes: string) {
    this.log("updateNotesTransferObjectAndModel - new notes: " + updatedNotes);

    // Guard against unnecessary updates
    if (this.notes != updatedNotes) {
      // update the transfer object
      this.notes = updatedNotes;
      //debugger;

      // update the model
      this.beerAnalysisHolder.getMyAnalysis().notes = updatedNotes;
      this.beerAnalysisHolder.checkForDifferences();
    }

  }

  private updateTotalExperienceTransferObjects(updatedTotalExperience: number) {
    this.log("updateTotalExperienceTransferObjects called, new value: " + updatedTotalExperience);
      this.totalexperience = updatedTotalExperience;
      if (updatedTotalExperience > 0)
        this.totalexperienceGUIValue = this.getRounded(2 * this.totalexperience);
      else
        this.totalexperienceGUIValue = 0;
  }
  updateTotalExperienceModelFromGUI(updatedTotalExperience: number) {
    this.log("updateTotalExperienceModelFromGUI called, new value: " + updatedTotalExperience);

    // update the transfer object if it changed
    if (this.totalexperience != updatedTotalExperience) {
      this.updateTotalExperienceTransferObjects(updatedTotalExperience);

      // update the model
      this.beerAnalysisHolder.getMyAnalysis().totalexperience = updatedTotalExperience / 2.0;
      this.beerAnalysisHolder.checkForDifferences();
    }
  }

  dismissLoading() {
    if (this.loading != null) {
      this.loading.dismiss();
      this.loading = null;
    }
  }

  // show current analysis instance
  loadMyAnalysis(shouldShowToast: boolean) {
    this.log("loadMyAnalysis called with shouldShowToast = " + shouldShowToast);

    let currentAnalysis: Analysis = this.beerAnalysisHolder.getMyAnalysis();
    // this.tweakAnalysisBeforeViewing(currentAnalysis);

    // Step 1: populate transfer objects

    // Step 1A - currentBaseFlavors
    this.updateColourFromAnalysis(currentAnalysis);

    // Step 1B - analysisCharacteristics and characteristicsKeyNames

    // Step 1C - totalexperience
    this.updateTotalExperienceFromAnalysis(currentAnalysis);

    // Step 1D - notes
    this.updateNotesFromAnalysis(currentAnalysis);

    this.updateSpiderValuesFromAnalysis(currentAnalysis);

    let currentAnalysisId = currentAnalysis.id;
    if (currentAnalysisId != undefined)
      this.analysisService.setAnalysisId(currentAnalysisId);
  }
  showMyAnalysis(shouldShowToast: boolean) {
    this.log("showMyAnalysis called with shouldShowToast = " + shouldShowToast);

    this.events.publish("set:colour", this.currentBaseFlavors.color);
    this.showIBAFromAnalysis();
    this.showMouthfeelFromMyAnalysis();
    this.showCharacteristicsFromMyAnalysis();
    this.showTotalExperienceFromMyAnalysis();
    this.events.publish("set:notes", this.notes);
    this.triggerSpiderUpdateFromBaseFlavors();
    this.events.publish("beer-detail:update:beer-aromas",this.characteristicsKeyNames);
    this.events.publish("beer-detail:update:rating-bar", { totalexperience: this.totalexperienceGUIValue});
  }

  updateBeerID(bID: string) {
    this.log("updateBeerID() => " + bID);
    //debugger;
    this.theBeerID = bID;
    this.analysisService.setBeerId(bID);
  }

  ngOnInit() {
    this.log("[ngOnInit] called");
    // setting last caller page
    this.events.subscribe("wishlist:present", this.wishlistPresentSubscription);
    this.events.subscribe("update:notes", this.updateNotesSubscription);
    this.events.subscribe("analysis:totalexperience", this.analysisTotalexperienceSubscription);
    this.events.subscribe("beer-analysis:show:beer-analysis-my", this.showBeerSubscription);
    this.events.subscribe("save:beer", this.saveBeerSubscription);
    this.events.subscribe("analyse:colourInput", this.analyseColourInputSubscription);
    this.events.subscribe("post:analysis", this.postAnalysisSubscription);

    this.analysisService.setLastPage("analysis");

    if (this.beerAnalysisHolder.hasActiveBeer())
      this.loadMyAnalysis(false);
  }
  ngOnChanges() {
    this.log("[ngOnChanges] called");
  }

  ngOnDestroy() {

    this.log("[ngOnDestroy] DESTROYING for beer ID " + this.theBeerID);
    this.events.unsubscribe("beer-analysis:show:beer-analysis-my");
    this.events.unsubscribe("post:analysis", this.postAnalysisSubscription);
    this.events.unsubscribe("similar-beers:show:beer-analysis", this.showBeerSubscription);
    this.events.unsubscribe("wishlist:present", this.wishlistPresentSubscription);

    this.events.unsubscribe("analyse:colourInput", this.analyseColourInputSubscription);
    this.events.unsubscribe("analysis:totalexperience", this.analysisTotalexperienceSubscription);
    this.events.unsubscribe("update:notes", this.updateNotesSubscription);

    this.events.unsubscribe("save:beer", this.saveBeerSubscription);

    this.events.unsubscribe("venue:deselected", this.venueDeselectedSubscription);
    this.events.unsubscribe("venue:selected", this.venueSelectedSubscription);
  }

  public updateView() {
    this.log("updateView() called");
    this.showMyAnalysis(false);
  }

  updateColourFromAnalysis(currentAnalysis) {
    this.log("updateColourFromAnalysis: PUB set:colour");
    let currentColour = currentAnalysis.colour;

    //added this one
    this.currentBaseFlavors.color = currentColour;
  }

  updateNotesFromAnalysis(currentAnalysis) {
    this.log("updateNotesFromAnalysis called");
    let currentNotes = currentAnalysis.notes;
    this.notes = currentNotes;
  }

  updateCC(ccInfo: any) {
    this.log("updateCC: color: " + ccInfo.color + " / clarity: " + ccInfo.clarity);

    this.currentBaseFlavors.color = ccInfo.color;
    this.currentBaseFlavors.clarity = ccInfo.clarity;
    //change a local value, that is coupled to the beer-hive
    this.triggerSpiderUpdateFromBaseFlavors();
  }

  showIBAFromAnalysis() {
    this.log("showIBAFromAnalysis called");
    let currentAnalysis = this.beerAnalysisHolder.getMyAnalysis();
    let intensity = (currentAnalysis.intensity === undefined) ? 0 : this.getRounded(currentAnalysis.intensity.valueOf());
    let body = (currentAnalysis.body === undefined) ? 0 : this.getRounded(currentAnalysis.body.valueOf());
    let astringency = (currentAnalysis.astringent === undefined) ? 0 : this.getRounded(currentAnalysis.astringent.valueOf());

    let ibaEvent = {
      "intensity": intensity,
      "body": body,
      "astringency": astringency
    };
    this.events.publish("update:iba", ibaEvent);
  }

  clearIBATransferObjectsAndPublishEvent() {
    this.log("clearIBATransferObjectsAndPublishEvent called");
    let intensity = 0;
    let body = 0;
    let astringency = 0;

    let ibaEvent = {
      "intensity": intensity,
      "body": body,
      "astringency": astringency
    };

    //debugger; //iba event 3 (clear)
    this.events.publish("update:iba", ibaEvent);
  }

  triggerSpiderUpdateFromBaseFlavors() {
    let hiveEvent = {
      "sweet": this.currentBaseFlavors.sweet,
      "sour": this.currentBaseFlavors.sour,
      "bitter": this.currentBaseFlavors.bitter,
      "hoppy": this.currentBaseFlavors.hoppy,
      "fruity": this.currentBaseFlavors.fruity,
      "malty": this.currentBaseFlavors.malty,
      "color": this.currentBaseFlavors.color,
      "clarity": this.currentBaseFlavors.clarity
    };
    this.events.publish("update:spider", hiveEvent);
  }
  updateSpiderValuesFromAnalysis(currentAnalysis) {
    this.log("updateSpiderValuesFromAnalysis called with " + JSON.stringify(currentAnalysis));
    // publish an event, check if values are present
    let sweetValue = currentAnalysis.sweet !== undefined ? this.getRounded(currentAnalysis.sweet) : 0;
    let sourValue = currentAnalysis.sour !== undefined ? this.getRounded(currentAnalysis.sour) : 0;
    let bitterValue = currentAnalysis.bitter !== undefined ? this.getRounded(currentAnalysis.bitter) : 0;
    let hoppyValue = currentAnalysis.hoppy !== undefined ? this.getRounded(currentAnalysis.hoppy) : 0;
    let fruityValue = currentAnalysis.fruity !== undefined ? this.getRounded(currentAnalysis.fruity) : 0;
    let maltyValue = currentAnalysis.malty !== undefined ? this.getRounded(currentAnalysis.malty) : 0;
    let colorValue = currentAnalysis.colour !== undefined ? this.getRounded(currentAnalysis.colour) : 0;

    this.currentBaseFlavors.sweet = sweetValue;
    this.currentBaseFlavors.sour = sourValue;
    this.currentBaseFlavors.bitter = bitterValue;
    this.currentBaseFlavors.hoppy = hoppyValue;
    this.currentBaseFlavors.fruity = fruityValue;
    this.currentBaseFlavors.malty = maltyValue;
    this.currentBaseFlavors.color = colorValue;
    this.currentBaseFlavors.clarity = -1;
  }

  clearSpiderValuesTransferObjectandPublishEvent() {

    this.log("clearSpiderValuesTransferObjectandPublishEvent called");

    //publish an event
    // this was 0; changed it to -1 for bug BEERHIVE-98
    let sweetValue = -1;
    let sourValue = -1;
    let bitterValue = -1;
    let hoppyValue = -1;
    let fruityValue = -1;
    let maltyValue = -1;
    let colorValue = -1; // this.currentBaseFlavors.color;// no color, invalid color

    this.currentBaseFlavors.sweet = sweetValue;
    this.currentBaseFlavors.sour = sourValue;
    this.currentBaseFlavors.bitter = bitterValue;
    this.currentBaseFlavors.hoppy = hoppyValue;
    this.currentBaseFlavors.fruity = fruityValue;
    this.currentBaseFlavors.malty = maltyValue;
    this.currentBaseFlavors.color = colorValue;
    this.currentBaseFlavors.clarity = -1;

    this.triggerSpiderUpdateFromBaseFlavors();
  }

  showMouthfeelFromMyAnalysis() {
    let currentAnalysis = this.beerAnalysisHolder.getMyAnalysis();
    //debugger;
    this.log("showMouthfeelFromMyAnalysis()=> " + this.bhutil.stringify(currentAnalysis));

    let alcohol = -1;
    let astringent = -1;
    let carbonation = -1;
    let intensity = -1;
    let body = -1;
    if (currentAnalysis.alcohol !== undefined)
      alcohol = currentAnalysis.alcohol.valueOf();
    if (currentAnalysis.astringent !== undefined)
      astringent = currentAnalysis.astringent.valueOf();
    if (currentAnalysis.body !== undefined)
      body = currentAnalysis.body.valueOf();
    if (currentAnalysis.carbonation !== undefined)
      carbonation = currentAnalysis.carbonation.valueOf();
    if (currentAnalysis.intensity !== undefined)
      intensity = currentAnalysis.intensity.valueOf();

    let source = 1;

    //create an event
    let mouthfeelEvent = {};
    mouthfeelEvent["alcohol"] = alcohol;
    mouthfeelEvent["astringent"] = astringent;
    mouthfeelEvent["body"] = body;
    mouthfeelEvent["carbonation"] = carbonation;
    mouthfeelEvent["intensity"] = intensity;
    mouthfeelEvent["source"] = source;

    //publish the event
    this.log("PUB update:mouthfeel 1");
    this.events.publish("update:mouthfeel", mouthfeelEvent);
    //catch the event in the beer-hive item (or add it to the update:aromas as an extra field)

  }

  showCharacteristicsFromMyAnalysis() {
    let currentAnalysis = this.beerAnalysisHolder.getMyAnalysis();
    this.log("showCharacteristicsFromMyAnalysis()=> " + this.bhutil.stringify(currentAnalysis));
    let characteristics = [];
    //debugger;//setting char
    if (currentAnalysis && currentAnalysis.characteristicsvalues) {
      for (let aChar of currentAnalysis.characteristicsvalues) {
        let charIndex = aChar.name.indexOf("_");//used to be aChar.indexOf()???
        if (charIndex != -1) {
          // there is an _ in the item name => aroma, ...
          // check for value (do not add if value == 0) => this means the item still has to be removed
          if (aChar.value > 0) {
            // fix for bug BEERHIVE-95 ???
            console.log("updateCharacteristicsFromAnalysis: ADDing " + aChar.name);
            characteristics.push(aChar.name);
          }
          else {
            // not adding this item
            console.log("updateCharacteristicsFromAnalysis: NOT adding " + aChar.name);
          }
        }
        else {
          characteristics.push(aChar.name);
        }
      }
      console.log("[beer-analysis.updateCharacteristicsFromAnalysis] - characteristics are: " + characteristics.join(","));
      this.characteristicsKeyNames = characteristics;
    }
    else {
      this.aromasActive = false;
    }
    //debugger;
    this.log("updateCharacteristicsFromAnalysis => PUB set:characteristics => " + currentAnalysis);
    //debugger;//pubchar2
    this.events.publish("set:characteristics", characteristics);

    //TODO TEST TEST TEST
    this.events.publish("set:shouldUpdate", true);
  }

  clearAromasTransferObjectsandPublishEvent() {
    this.log("clearAromasTransferObjectsandPublishEvent()");

    // fix for BEERHIVE-197
    // the line below aromasActive = false was not enough to hide the list of aromas!
    this.aromasActive = false;
    // we needed to add this line, explicitly setting the aromas to an empty list!
//    this.events.publish("update:aromas", []);
    this.characteristicsKeyNames = [];

    //create an event
    let mouthfeelEvent = {};
    mouthfeelEvent["alcohol"] = -1;
    mouthfeelEvent["astringent"] = -1;
    mouthfeelEvent["body"] = -1;
    mouthfeelEvent["carbonation"] = -1;
    mouthfeelEvent["intensity"] = -1;
    mouthfeelEvent["source"] = 3;

    //publish the event
    this.log("PUB update:mouthfeel 3");
    this.events.publish("update:mouthfeel", mouthfeelEvent);

  }

  updateTotalExperienceFromAnalysis(currentAnalysis) {
    this.log("updateTotalExperienceFromAnalysis()=> " + this.bhutil.stringify(currentAnalysis));

    this.ratingBarActive = true;
    this.updateTotalExperienceTransferObjects(currentAnalysis.totalexperience);

  }
  showTotalExperienceFromMyAnalysis() {
    this.log("showTotalExperienceFromMyAnalysis called with totalexperienceGUIValue = " + this.totalexperienceGUIValue);
    let totalExperienceEvent = {
      "totalexperience": this.totalexperienceGUIValue
    }

    //this.ignoreTotalExperienceValue = true;//otherwise, the response will lead to another call!
    this.events.publish("beer-analysis-my-summary:update:totalexperience", totalExperienceEvent);
    //event will be received by "rating-bar" component(s)
  }

  clearTotalExperienceTransferObjectAndPublishEvent() {

    this.log("clearTotalExperience()");

    this.updateTotalExperienceTransferObjects(0);
    this.ratingBarActive = true;

    let totalExperienceEvent = {
      "totalexperience": this.totalexperienceGUIValue
    }
    this.log("PUB clear:totalexperience; setting value to 0");
    this.events.publish("clear:totalexperience", totalExperienceEvent);
    //event will be received by "rating-bar" component(s)
  }

  private log(s): void {
    console.log("[beer-analysis-my-summary] " + s);
  }
}
